import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import MultiSelectBox from '../../Common/MultiSelectBox/MultiSelectBox';
import '../Reports.scss';
import Button from '../../Common/Button';
import { CsvToIntArray } from '../../../helpers/strings';
import DatePicker from '../../Common/DatePicker/DatePicker';
import { getDateValue } from '../../../helpers/reports';

const COLUMNS_TO_EXCLUDE = [
    { id: 'Area', name: 'Area' },
    { id: 'Brand', name: 'Brand' },
    { id: 'LocationType', name: 'Location Type' },
    { id: 'ManagementCompany', name: 'Management Company' },
    { id: 'Region', name: 'Region' }
];

export class LocationTaskSummaryFilters extends React.Component {

    constructor(props, context) {
        super(props, context);

        const initialFormSelections = {
            areaIds: '',
            brandIds: '',
            continentIds: '',
            countryIds: '',
            excludedColumnIds: '',
            locationTypeIds: '',
            managementCompanyIds: '',
            regionIds: '',
            openingDateStart: '',
            openingDateEnd: ''
        }

        this.state = {
            emptyForm: initialFormSelections,
            showMoreFields: false,

            excludedColumnOptions: COLUMNS_TO_EXCLUDE,

            areaIds: initialFormSelections.areaIds,
            brandIds: initialFormSelections.brandIds,
            continentIds: initialFormSelections.continentIds,
            countryIds: initialFormSelections.countryIds,
            excludedColumnIds: initialFormSelections.excludedColumnIds,
            locationTypeIds: initialFormSelections.locationTypeIds,
            managementCompanyIds: initialFormSelections.managementCompanyIds,
            regionIds: initialFormSelections.regionIds,
            openingDateStart: initialFormSelections.openingDateStart,
            openingDateEnd: initialFormSelections.openingDateEnd
        }
    }

    componentDidMount = async () => {
        /**
         * Must set defaults before registering report filter functions, because
         * these defaults might be overridden by mapToForm values coming from
         * the API when opening a scheduled report.
         */
        this.setDefaults();
        /**
         * On mount, register all of this form's callbacks so the parent is able
         * to use them when its wrapping UI indicates these actions should be
         * performed.
         */
        this.props.registerReportFilterFunctions(
            this.props.report.id,
            this.clearForm,
            this.validateForm,
            this.mapToForm,
            this.mapToApiRequest
        );
    }

    setDefaults = () => {
        let newState = { ...this.state };

        const appState = this.props.savedFilterValues;
        if (appState) {
            // Set new state to values from redux app state
            newState = {
                ...newState,

                areaIds: appState.areaIds ? appState.areaIds : newState.areaIds,
                brandIds: appState.brandIds ? appState.brandIds : newState.brandIds,
                continentIds: appState.continentIds ? appState.continentIds : newState.continentIds,
                countryIds: appState.countryIds ? appState.countryIds : newState.countryIds,
                excludedColumnIds: appState.excludedColumnIds ? appState.excludedColumnIds : newState.excludedColumnIds,
                locationTypeIds: appState.locationTypeIds ? appState.locationTypeIds : newState.locationTypeIds,
                managementCompanyIds: appState.managementCompanyIds ? appState.managementCompanyIds : newState.managementCompanyIds,
                regionIds: appState.regionIds ? appState.regionIds : newState.regionIds,
                openingDateStart: appState.openingDateStart ? appState.openingDateStart : newState.openingDateStart,
                openingDateEnd: appState.openingDateEnd ? appState.openingDateEnd : newState.openingDateEnd
            }
        }

        this.setState({
            ...this.state,
            ...newState
        });
    }

    clearForm = () => {
        this.setState({
            ...this.state,
            ...this.state.emptyForm
        });
    }

    validateForm = () => {
        return true;
    }

    mapToForm = (reportRequest) => {
        const form = { ...this.state.emptyForm };

        form.areaIds = (reportRequest.areaIds) ? reportRequest.areaIds.join(',') : form.areaIds;
        form.brandIds = (reportRequest.brandIds) ? reportRequest.brandIds.join(',') : form.brandIds;
        form.continentIds = (reportRequest.continentIds) ? reportRequest.continentIds.join(',') : form.continentIds;
        form.countryIds = (reportRequest.countryIds) ? reportRequest.countryIds.join(',') : form.countryIds;
        form.excludedColumnIds = (reportRequest.columnKeys) ? reportRequest.columnKeys.join(',') : form.columnKeys;
        form.locationTypeIds = (reportRequest.locationTypeIds) ? reportRequest.locationTypeIds.join(',') : form.locationTypeIds;
        form.managementCompanyIds = (reportRequest.managementCompanyIds) ? reportRequest.managementCompanyIds.join(',') : form.managementCompanyIds;
        form.regionIds = (reportRequest.regionIds) ? reportRequest.regionIds.join(',') : form.regionIds;
        form.openingDateStart = getDateValue(reportRequest.openingDateStart);
        form.openingDateEnd = getDateValue(reportRequest.openingDateEnd);

        this.setState({
            ...this.state,
            ...form
        });
    }

    /**
     * When an API request is going to be made, we also want to save the state
     * of our form entries into redux, so that saved state can be loaded when
     * this report is scheduled or simply reloaded later.  To accomplish that
     * from the centralized ReportDataHelper, we return both the apiRequest
     * object as well as the current state of our form.  This separation allows
     * for differences in the apiRequest format/naming conventions and those of
     * the form fields.
     */
    mapToApiRequest = () => {

        let columnKeys = null;
        if (this.state.excludedColumnIds && this.state.excludedColumnIds.trim().length > 0) {
            columnKeys = this.state.excludedColumnIds.split(',');
        }

        const request = {
            reportId: this.props.report.id,

            areaIds: CsvToIntArray(this.state.areaIds),
            brandIds: CsvToIntArray(this.state.brandIds),
            continentIds: CsvToIntArray(this.state.continentIds),
            countryIds: CsvToIntArray(this.state.countryIds),
            columnKeys: columnKeys,
            locationTypeIds: CsvToIntArray(this.state.locationTypeIds),
            managementCompanyIds: CsvToIntArray(this.state.managementCompanyIds),
            regionIds: CsvToIntArray(this.state.regionIds),
            openingDateStart: this.state.openingDateStart,
            openingDateEnd: this.state.openingDateEnd
        };

        return {
            apiRequest: request,
            formValues: this.state
        };
    }

    onFieldChange = (name, value) => {
        this.setState({
            ...this.state,
            [name]: value
        });
    }

    onTextInputChange = (e) => {
        this.onFieldChange(e.target.name, e.target.value);
    }

    onDropdownChange = (value, name) => {
        this.onFieldChange(name, value);
    }

    onDatePickerChange = (date, name) => {
        this.onFieldChange(name, date);
    }

    onToggleShowMore = () => {
        this.setState({
            showMoreFields: !this.state.showMoreFields
        });
    }

    render() {
        const showMoreText = this.state.showMoreFields ? "Show Less Filters" : "Show More Filters";

        return (
            <form id="subForm" noValidate className={this.props.isFormValid ? "" : "was-validated"}>

                <div className="row">
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="brandIds"
                            label="Brand" idField="id" valueField="name"
                            options={this.props.filters.brands}
                            selectedValues={this.state.brandIds.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="locationTypeIds"
                            label="Location Type" idField="id" valueField="name"
                            options={this.props.filters.locationTypes}
                            selectedValues={this.state.locationTypeIds.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="managementCompanyIds"
                            label="Management Company" idField="id" valueField="name"
                            options={this.props.filters.managementCompanies}
                            selectedValues={this.state.managementCompanyIds.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} />
                    </div>
                </div>
                {
                    this.state.showMoreFields &&
                    <Fragment>
                        <div className="row">
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="areaIds"
                                    label="Area" idField="id" valueField="name"
                                    options={this.props.filters.areas}
                                    selectedValues={this.state.areaIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="excludedColumnIds"
                                    label="Include Columns" idField="id" valueField="name"
                                    options={this.state.excludedColumnOptions}
                                    selectedValues={this.state.excludedColumnIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="countryIds"
                                    label="Country" idField="id" valueField="name"
                                    options={this.props.filters.countries}
                                    selectedValues={this.state.countryIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="continentIds"
                                    label="Continent" idField="id" valueField="name"
                                    options={this.props.filters.continents}
                                    selectedValues={this.state.continentIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="regionIds"
                                    label="Region" idField="id" valueField="name"
                                    options={this.props.filters.regions}
                                    selectedValues={this.state.regionIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                            <div className="col-sm-12 col-md-2">
                                <DatePicker showIcon name="openingDateStart"
                                    value={this.state.openingDateStart}
                                    label="Opening Date Start"
                                    onChange={this.onDatePickerChange} />
                            </div>
                            <div className="col-sm-12 col-md-2">
                                <DatePicker showIcon name="openingDateEnd"
                                    value={this.state.openingDateEnd}
                                    label="End"
                                    onChange={this.onDatePickerChange} />
                            </div>
                        </div>
                    </Fragment>
                }
                <div className="row text-end">
                    <div className="col-sm-12">
                        <Button name="showMore" type="button" bootstrapClass="link" text={showMoreText} onClick={this.onToggleShowMore} />
                    </div>
                </div>
            </form>
        );
    }
}

LocationTaskSummaryFilters.propTypes = {
    report: PropTypes.object.isRequired,
    filters: PropTypes.object.isRequired,
    urlParams: PropTypes.object.isRequired,
    savedFilterValues: PropTypes.object.isRequired,
    isFormValid: PropTypes.bool.isRequired,

    registerReportFilterFunctions: PropTypes.func.isRequired
};

export default LocationTaskSummaryFilters;
